import { useCallback, useEffect } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
    currentImageSliceOrientationPositionsState,
    sideBySideImageSliceOrientationPositionsState,
    areEnvironmentsLockedState,
} from '~/state/atoms/viewerAtoms';
import {
    environmentState,
    lowerSideViewState,
    mainViewState,
    secondaryEnvironmentState,
    secondaryLowerSideViewState,
    secondaryMainViewState,
    secondaryUpperSideViewState,
    upperSideViewState,
    viewManagerState,
} from '~/state/atoms/engineAtoms';
import useViewManagerEvents from '~/hooks/useViewManagerEvents';
import useViewportEnvironment from '../useViewportEnvironment';
import useViewportSecondaryEnvironment from '../useViewportSecondaryEnvironment';
import { lastMousePositionState } from '~/state/atoms/viewerAtoms';
import { vec2 } from 'gl-matrix';
import useUpdateCursorRelatedStates from './useUpdateCursorRelatedStates';
import { PlaneView, PlaneViewType } from '~/engine/view/views/PlaneView';

export default function usePlaneIntersectionPointChanged(
    view: PlaneView | null
) {
    const viewManager = useRecoilValue(viewManagerState);
    const environment = useRecoilValue(environmentState);
    const secondaryEnvironment = useRecoilValue(secondaryEnvironmentState);
    const env =
        view?.type === PlaneViewType.Primary
            ? environment
            : secondaryEnvironment;
    const areEnvironmentsLocked = useRecoilValue(areEnvironmentsLockedState);

    const mainView = useRecoilValue(mainViewState);
    const upperSideView = useRecoilValue(upperSideViewState);
    const lowerSideView = useRecoilValue(lowerSideViewState);

    const secondaryMain = useRecoilValue(secondaryMainViewState);
    const secondaryUpper = useRecoilValue(secondaryUpperSideViewState);
    const secondaryLower = useRecoilValue(secondaryLowerSideViewState);

    const lastMousePosition = useRecoilValue(lastMousePositionState);

    const { getEnvImagePositions } = useViewportEnvironment();
    const { getSecEnvImagePositions } = useViewportSecondaryEnvironment();
    const { handleIntersectionPointSync } = useViewManagerEvents();

    const updateCursorRelatedStates = useUpdateCursorRelatedStates(env);

    const [
        currentImageSliceOrientationPositions,
        setCurrentImageSliceOrientationPositions,
    ] = useRecoilState(currentImageSliceOrientationPositionsState);

    const [
        sideBySideImageSliceOrientationPositions,
        setSideBySideImageSliceOrientationPositions,
    ] = useRecoilState(sideBySideImageSliceOrientationPositionsState);

    const handlePrimaryImageSliceChange = useCallback(() => {
        setCurrentImageSliceOrientationPositions(
            getEnvImagePositions(currentImageSliceOrientationPositions)
        );
    }, [
        currentImageSliceOrientationPositions,
        getEnvImagePositions,
        setCurrentImageSliceOrientationPositions,
    ]);

    const handleSecondaryImageSliceChange = useCallback(() => {
        setSideBySideImageSliceOrientationPositions(
            getSecEnvImagePositions(sideBySideImageSliceOrientationPositions)
        );
    }, [
        sideBySideImageSliceOrientationPositions,
        getSecEnvImagePositions,
        setSideBySideImageSliceOrientationPositions,
    ]);

    useEffect(() => {
        if (!viewManager || !view || !lastMousePosition) {
            return;
        }

        const handleCursorUpdate = () => {
            const canvas = view.canvas;
            if (!canvas) {
                return;
            }

            const rect = canvas.getBoundingClientRect();
            const { clientX, clientY } = lastMousePosition;

            if (
                clientX < rect.left ||
                clientX > rect.right ||
                clientY < rect.top ||
                clientY > rect.bottom
            ) {
                return;
            }

            const patientCoords = view.clientToPatientCoordinates(
                vec2.fromValues(clientX, clientY)
            );
            if (patientCoords) {
                updateCursorRelatedStates(patientCoords);
            }
        };

        viewManager.addEventListener(
            'planeIntersectionPointChanged',
            handleCursorUpdate
        );

        return () => {
            viewManager.removeEventListener(
                'planeIntersectionPointChanged',
                handleCursorUpdate
            );
        };
    }, [viewManager, view, lastMousePosition, updateCursorRelatedStates]);

    useEffect(() => {
        if (!viewManager || !mainView || !upperSideView || !lowerSideView) {
            return;
        }

        viewManager.addEventListener(
            'planeIntersectionPointChanged',
            handlePrimaryImageSliceChange
        );

        return () => {
            viewManager.removeEventListener(
                'planeIntersectionPointChanged',
                handlePrimaryImageSliceChange
            );
        };
    }, [
        viewManager,
        mainView,
        upperSideView,
        lowerSideView,
        handlePrimaryImageSliceChange,
    ]);

    useEffect(() => {
        if (
            !viewManager ||
            !secondaryEnvironment ||
            !secondaryMain ||
            !secondaryUpper ||
            !secondaryLower
        ) {
            return;
        }
        handleSecondaryImageSliceChange(); // has to be here to set initial positions
        viewManager.addEventListener(
            'planeIntersectionPointChanged',
            handleSecondaryImageSliceChange
        );

        if (areEnvironmentsLocked) {
            viewManager.addEventListener(
                'planeIntersectionPointChanged',
                handleIntersectionPointSync
            );
        }

        return () => {
            viewManager.removeEventListener(
                'planeIntersectionPointChanged',
                handleSecondaryImageSliceChange
            );
            if (areEnvironmentsLocked) {
                viewManager.removeEventListener(
                    'planeIntersectionPointChanged',
                    handleIntersectionPointSync
                );
            }
        };
    }, [
        viewManager,
        secondaryEnvironment,
        areEnvironmentsLocked,
        secondaryMain,
        secondaryUpper,
        secondaryLower,
        handleIntersectionPointSync,
        handleSecondaryImageSliceChange,
    ]);
}
